import { useEffect, useState, type ReactElement } from 'react';
import { formApi, type TaxForm, ApiError } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { Button, Card, EmptyState, ErrorNote, PageHeader } from '../ui';

type FieldType = 'text' | 'textarea' | 'date' | 'money' | 'number' | 'select';

interface Field {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

interface FormDef {
  key: string;
  label: string;
  intro: string;
  fields: Field[];
}

// Geführte Anträge: pro Typ eigene, sinnvolle Felder statt einem generischen Freitextfeld.
const FORMS: FormDef[] = [
  {
    key: 'stundung',
    label: 'Stundungsantrag',
    intro: 'Du beantragst, eine fällige Steuer später zahlen zu dürfen.',
    fields: [
      { key: 'steuerart', label: 'Welche Steuer?', type: 'select', required: true, options: [
        { value: 'Umsatzsteuer', label: 'Umsatzsteuer' },
        { value: 'Einkommensteuer', label: 'Einkommensteuer' },
        { value: 'Gewerbesteuer', label: 'Gewerbesteuer' },
        { value: 'Körperschaftsteuer', label: 'Körperschaftsteuer' },
        { value: 'Sonstige', label: 'Sonstige' },
      ] },
      { key: 'betrag', label: 'Zu stundender Betrag', type: 'money', required: true, hint: 'Der Betrag laut Bescheid.' },
      { key: 'faellig_am', label: 'Ursprünglich fällig am', type: 'date', required: true },
      { key: 'stundung_bis', label: 'Stundung gewünscht bis', type: 'date', required: true, hint: 'Bis wann brauchst du Aufschub?' },
      { key: 'anliegen', label: 'Begründung', type: 'textarea', required: true,
        placeholder: 'z. B. vorübergehender Liquiditätsengpass durch ausstehende Kundenzahlungen…',
        hint: 'Warum kannst du gerade nicht zahlen? Kurz und sachlich.' },
    ],
  },
  {
    key: 'einspruch',
    label: 'Einspruch',
    intro: 'Du legst Einspruch gegen einen Steuerbescheid ein. Frist: 1 Monat ab Bekanntgabe.',
    fields: [
      { key: 'bescheid_art', label: 'Gegen welchen Bescheid?', type: 'text', required: true, placeholder: 'z. B. Umsatzsteuerbescheid 2024' },
      { key: 'bescheid_datum', label: 'Datum des Bescheids', type: 'date', required: true },
      { key: 'aktenzeichen', label: 'Aktenzeichen / Bescheid-Nr.', type: 'text', hint: 'Falls auf dem Bescheid angegeben.' },
      { key: 'anliegen', label: 'Begründung des Einspruchs', type: 'textarea', required: true,
        placeholder: 'z. B. Betriebsausgaben in Höhe von … wurden nicht berücksichtigt…',
        hint: 'Was ist aus deiner Sicht falsch?' },
    ],
  },
  {
    key: 'ratenzahlung',
    label: 'Ratenzahlung',
    intro: 'Du beantragst, eine Forderung in Raten zahlen zu dürfen.',
    fields: [
      { key: 'gesamtbetrag', label: 'Gesamtbetrag', type: 'money', required: true },
      { key: 'rate', label: 'Gewünschte monatliche Rate', type: 'money', required: true },
      { key: 'erste_rate', label: 'Erste Rate ab', type: 'date', required: true },
      { key: 'anliegen', label: 'Begründung', type: 'textarea', required: true,
        placeholder: 'z. B. Die Einmalzahlung würde den laufenden Betrieb gefährden…',
        hint: 'Warum ist eine Ratenzahlung nötig?' },
    ],
  },
  {
    key: 'fristverlaengerung',
    label: 'Fristverlängerung',
    intro: 'Du bittest um mehr Zeit für die Abgabe einer Erklärung.',
    fields: [
      { key: 'erklaerung', label: 'Welche Erklärung / Frist?', type: 'text', required: true, placeholder: 'z. B. Einkommensteuererklärung 2024' },
      { key: 'frist_bis', label: 'Neue Frist gewünscht bis', type: 'date', required: true },
      { key: 'anliegen', label: 'Begründung', type: 'textarea', required: true,
        placeholder: 'z. B. noch ausstehende Belege von Dritten…',
        hint: 'Warum brauchst du länger?' },
    ],
  },
  {
    key: 'berichtigung',
    label: 'Berichtigung',
    intro: 'Du korrigierst eine bereits abgegebene Erklärung oder Anmeldung.',
    fields: [
      { key: 'erklaerung', label: 'Welche Erklärung?', type: 'text', required: true, placeholder: 'z. B. Umsatzsteuer-Voranmeldung Q1 2025' },
      { key: 'zeitraum', label: 'Zeitraum', type: 'text', placeholder: 'z. B. 01–03/2025' },
      { key: 'anliegen', label: 'Was soll berichtigt werden?', type: 'textarea', required: true,
        placeholder: 'z. B. Eine Eingangsrechnung über … wurde versehentlich nicht erfasst…',
        hint: 'Beschreibe die Änderung gegenüber dem bisherigen Stand.' },
    ],
  },
];

function fmtValue(f: Field, v: string): string {
  if (!v) return '—';
  if (f.type === 'money') return `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
  if (f.type === 'date') return new Date(v).toLocaleDateString('de-DE');
  return v;
}

export default function Forms(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const [forms, setForms] = useState<TaxForm[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [locked, setLocked] = useState(false);
  const [open, setOpen] = useState<FormDef | null>(null);
  const [prefilled, setPrefilled] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);

  function load() {
    if (!active) return;
    formApi.list(active.id).then(setForms).catch((e) => { if (e instanceof ApiError && e.status === 402) setLocked(true); else setError(e); });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  async function startForm(def: FormDef) {
    if (!active) return;
    setOpen(def);
    setValues({});
    setTouched(false);
    setKeywords('');
    setDraftError(null);
    try {
      const t = await formApi.template(active.id, def.key);
      setPrefilled(t.prefilled);
    } catch { setPrefilled({}); }
  }

  // KI-Begruendung: aus Stichworten + bereits ausgefuellten Feldern einen Text formulieren.
  async function generateDraft() {
    if (!active || !open) return;
    setDrafting(true);
    setDraftError(null);
    try {
      const fields: Record<string, string> = {};
      for (const f of open.fields) {
        if (f.key === 'anliegen') continue;
        const v = values[f.key];
        if (v?.trim()) fields[f.label] = fmtValue(f, v);
      }
      const kw = keywords.trim();
      const text = await formApi.draft(active.id, open.key, kw ? { keywords: kw, fields } : { fields });
      setValues((v) => ({ ...v, anliegen: text }));
    } catch (e) {
      setDraftError(e instanceof ApiError ? e.message : 'Vorschlag fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setDrafting(false);
    }
  }

  const missing = open
    ? open.fields.filter((f) => f.required && !values[f.key]?.trim()).map((f) => f.label)
    : [];

  async function save() {
    if (!active || !open) return;
    if (missing.length) { setTouched(true); return; }
    setBusy(true);
    try {
      await formApi.create(active.id, open.key, { ...prefilled, ...values });
      setOpen(null);
      load();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  async function remove(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Formular löschen?', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    await formApi.remove(active.id, id);
    load();
  }

  async function showPdf(id: string) {
    if (!active) return;
    setPdfBusyId(id);
    setError(null);
    try {
      const blob = await formApi.pdf(active.id, id, true);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) { setError(e); } finally { setPdfBusyId(null); }
  }

  async function downloadPdf(id: string, type: string) {
    if (!active) return;
    setPdfBusyId(id);
    setError(null);
    try {
      const blob = await formApi.pdf(active.id, id, false);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e); } finally { setPdfBusyId(null); }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }

  if (locked) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Formulare" />
        <EmptyState title="Steuer-Formulare ab Professional" hint="Stundung, Einspruch, Ratenzahlung & mehr." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Formulare" subtitle="Anträge ans Finanzamt – vorausgefüllt aus deinen Stammdaten." />
      {error ? <div className="mb-4"><ErrorNote error={error} /></div> : null}

      <Card className="mb-6">
        <div className="mb-2 text-sm font-medium text-gray-700">Neues Formular</div>
        <div className="flex flex-wrap gap-2">
          {FORMS.map((t) => (
            <Button key={t.key} variant="secondary" onClick={() => startForm(t)}>{t.label}</Button>
          ))}
        </div>
      </Card>

      {forms.length === 0 ? (
        <EmptyState title="Noch keine Formulare" hint="Wähle oben einen Antragstyp." />
      ) : (
        <div className="space-y-2">
          {forms.map((f) => (
            <Card key={f.id} className="flex items-center justify-between !p-4">
              <div>
                <div className="font-medium text-gray-900">{FORMS.find((t) => t.key === f.type)?.label ?? f.type}</div>
                <div className="text-xs text-gray-500">{f.status === 'submitted' ? 'eingereicht' : 'Entwurf'} · {new Date(f.createdAt).toLocaleDateString('de-DE')}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => showPdf(f.id)} busy={pdfBusyId === f.id} disabled={pdfBusyId === f.id}>Ansehen</Button>
                <Button variant="secondary" onClick={() => downloadPdf(f.id, f.type)} disabled={pdfBusyId === f.id}>PDF</Button>
                <Button variant="ghost" onClick={() => remove(f.id)}>Löschen</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/60 p-4">
          <div className="mx-auto flex h-full w-full max-w-4xl flex-col rounded-2xl bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h2 className="text-base font-semibold text-gray-900">Antrags-Vorschau</h2>
              <Button variant="secondary" onClick={closePreview}>Schließen</Button>
            </div>
            <iframe title="Antrags-PDF" src={previewUrl} className="min-h-0 flex-1 rounded-b-2xl" />
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white">
            <div className="border-b border-gray-100 px-6 pb-4 pt-6">
              <h2 className="text-lg font-bold text-gray-900">{open.label}</h2>
              <p className="mt-1 text-sm text-gray-500">{open.intro}</p>
            </div>

            <div className="space-y-4 overflow-y-auto px-6 py-5">
              <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Aus deinen Stammdaten</div>
                <div><strong>Firma:</strong> {prefilled['firma'] || '—'}</div>
                <div><strong>Steuernummer:</strong> {prefilled['steuernummer'] || '—'}</div>
                <div><strong>Adresse:</strong> {prefilled['adresse'] || '—'}</div>
              </div>

              {open.fields.map((f) => {
                const showError = touched && f.required && !values[f.key]?.trim();
                const base = 'w-full rounded-xl border px-4 py-2.5 outline-none focus:ring-2 ' +
                  (showError ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200');
                return (
                  <label key={f.key} className="block">
                    <span className="mb-1.5 block text-sm font-medium text-gray-700">
                      {f.label}{f.required ? <span className="text-red-500"> *</span> : null}
                    </span>
                    {f.type === 'textarea' ? (
                      <textarea rows={3} value={values[f.key] ?? ''} placeholder={f.placeholder}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={base} />
                    ) : f.type === 'select' ? (
                      <select value={values[f.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={base}>
                        <option value="">Bitte wählen…</option>
                        {f.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input
                        type={f.type === 'money' || f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                        inputMode={f.type === 'money' ? 'decimal' : undefined}
                        step={f.type === 'money' ? '0.01' : undefined}
                        value={values[f.key] ?? ''} placeholder={f.placeholder}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={base} />
                    )}
                    {f.hint ? <span className="mt-1 block text-xs text-gray-400">{f.hint}</span> : null}
                    {showError ? <span className="mt-1 block text-xs text-red-500">Bitte ausfüllen.</span> : null}

                    {f.key === 'anliegen' && (
                      <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
                          <span aria-hidden>✨</span> KI-Hilfe – kein Amtsdeutsch nötig
                        </div>
                        <p className="mt-0.5 text-xs text-blue-700/80">
                          Tippe ein paar Stichworte (oder lass es leer) – die KI formuliert daraus eine saubere Begründung. Du kannst sie danach frei anpassen.
                        </p>
                        <textarea
                          rows={2}
                          value={keywords}
                          onChange={(e) => setKeywords(e.target.value)}
                          placeholder="z. B. großer Kunde zahlt verspätet, Miete und Löhne fällig"
                          className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <Button variant="secondary" onClick={generateDraft} busy={drafting} disabled={drafting}>
                            {values['anliegen']?.trim() ? '↻ Neu formulieren' : '✨ Vorschlag generieren'}
                          </Button>
                          <span className="text-xs text-blue-700/70">{drafting ? 'Formuliert…' : 'Schreibt direkt ins Feld oben'}</span>
                        </div>
                        {draftError ? <span className="mt-1 block text-xs text-red-500">{draftError}</span> : null}
                      </div>
                    )}
                  </label>
                );
              })}

              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Vorschau</div>
                <dl className="space-y-1 text-sm">
                  {open.fields.map((f) => (
                    <div key={f.key} className="flex gap-2">
                      <dt className="min-w-[40%] text-gray-500">{f.label}</dt>
                      <dd className="text-gray-800">{fmtValue(f, values[f.key] ?? '')}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-6 py-4">
              <span className="text-xs text-gray-400">{missing.length ? `${missing.length} Pflichtfeld(er) offen` : 'Alle Pflichtfelder ausgefüllt'}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setOpen(null)}>Abbrechen</Button>
                <Button onClick={save} busy={busy} disabled={busy}>Speichern</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
